/**
 * INTERMEDIATE band overlay (Macroblock #17, Phase B/C) — React/SVG, lightweight.
 *
 * Rendered ONLY in the INTERMEDIATE LOD band (OVERVIEW_MAX_ZOOM < zoom <=
 * DETAIL_MIN_ZOOM). Its single job is to communicate CONTENT PRESENCE and which
 * territory you are approaching — NOT to display the content itself. It draws:
 *
 *   - the real territory footprint (exact parcel union) with a subtle accent
 *   - a restrained perimeter outline
 *   - the territory display name where legible (suppressed when too small)
 *   - a restrained "has public content" cue derived from authoritative snapshot
 *     facts (has_publication / placement_count > 0)
 *
 * It NEVER renders the LOD0 brand image, the full Canvas, Canvas pixels, Frozen
 * Artwork pixels, fake thumbnails, or synthetic placeholders. Everything here is
 * derived from the deterministic public snapshot contract + authoritative parcel
 * geometry. Pointer-transparent so pan/zoom/selection pass through to the canvas.
 */
import { memo } from "react";

import { ownerColor, worldToScreen, type Camera } from "./camera";
import {
  unionBoundsWorld,
  unionScreenRects,
  type UnitRect,
} from "./brand-image-geometry";

export interface IntermediateTerritory {
  identityId: string;
  displayName: string | null;
  units: UnitRect[];
  owner: string;
  hasContent: boolean;
}

// Below this on-screen union size (CSS px) the content cue would be illegible/
// noisy, so it is suppressed — the footprint accent still communicates presence.
const CUE_MIN_UNION_PX = 40;

const TerritoryMark = memo(function TerritoryMark({
  item,
  camera,
}: {
  item: IntermediateTerritory;
  camera: Camera;
}) {
  const rects = unionScreenRects(camera, item.units);
  const wb = unionBoundsWorld(item.units);
  if (rects.length === 0 || !wb) return null;
  const tl = worldToScreen(camera, wb.x, wb.y);
  const frame = { x: tl.x, y: tl.y, w: wb.w * camera.scale, h: wb.h * camera.scale };
  const minPx = Math.min(frame.w, frame.h);

  const accent = ownerColor(item.owner);
  // Claudio: at the second (intermediate) zoom the parcel NAME is intentionally
  // NOT drawn — the visitor should see only the Canvas content (rendered by the
  // publication layer) and, for 3D territories, the ENTER 3D affordance.
  const showCue = item.hasContent && minPx >= CUE_MIN_UNION_PX;

  return (
    <g
      data-testid={`grid-intermediate-${item.identityId}`}
      data-has-content={item.hasContent ? "true" : "false"}
    >
      {/* Exact footprint — very subtle accent fill so the territory reads as a
          shape without competing with the real DETAIL content. */}
      {rects.map((r, i) => (
        <rect
          key={i}
          x={r.x}
          y={r.y}
          width={r.w}
          height={r.h}
          fill={accent}
          fillOpacity={0.08}
        />
      ))}
      {/* Restrained perimeter around the union bounds. */}
      <rect
        data-testid="grid-intermediate-perimeter"
        x={frame.x}
        y={frame.y}
        width={frame.w}
        height={frame.h}
        fill="none"
        stroke={accent}
        strokeOpacity={0.55}
        strokeWidth={1.5}
      />
      {showCue && (
        <circle
          data-testid="grid-intermediate-content-cue"
          cx={frame.x + frame.w - 10}
          cy={frame.y + 10}
          r={4}
          fill={accent}
          fillOpacity={0.9}
        />
      )}
    </g>
  );
});

/**
 * `camera` is non-null ONLY in the intermediate band (the GridViewer publishes it
 * between the overview cutoff and the detail floor), so at overview/detail zoom
 * this renders nothing and hands off cleanly to the adjacent LOD.
 */
export const GridIntermediateLayer = memo(function GridIntermediateLayer({
  camera,
  items,
}: {
  camera: Camera | null;
  items: IntermediateTerritory[];
}) {
  if (!camera || items.length === 0) return null;
  return (
    <svg
      data-testid="grid-intermediate-layer"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      {items.map((item) => (
        <TerritoryMark key={item.identityId} item={item} camera={camera} />
      ))}
    </svg>
  );
});
